/**
 * The demo console's reports (W1) — the fixture half of `acs_service/reports.py`.
 *
 * Two promises, and a guard for each:
 *
 *  1. **The catalogue is the server's.** `reports-catalog.json` is MEASURED from the Python
 *     definitions (`tests/test_acs_w1_reports.py::test_the_console_fixture_catalogue_is_the_servers`
 *     regenerates and compares it), so the demo cannot offer a report, a column or a filter the live
 *     console would not.
 *  2. **The rows come from the SAME fixtures the other pages show.** Rather than a second copy of the
 *     fleet, each report pages through the provider's own list methods (`listDevices`, `listJobs`, …)
 *     and maps the rows to the catalogue's columns. A device the Devices page shows is a device the
 *     device report lists.
 *
 * What the demo does NOT do, and says so: it has no `xlsx` writer (the page disables that button when
 * `dataSource` is `fixture`), the fixture devices carry no tags (so the `tag` filter matches nothing
 * and the `tags` column is empty), and it does not filter the catalogue by the caller's source
 * capability — the server does that, and the demo's accounts hold a minimal capability set.
 */

import { FIXTURE_EVENTS } from './records';
import { FIXTURE_DEVICES } from './devices';
import { parseReportCatalog } from '../api/conform';
import type { ConsoleApi } from '../api/consoleApi';
import type { ReportCatalog, ReportInfo, ReportQuery, ReportResult } from '../api/types';
import catalogJson from './reports-catalog.json';

export const FIXTURE_REPORT_CATALOG: ReportCatalog = parseReportCatalog(catalogJson);

/** The slice of the provider a report reads. Declared so the builder cannot reach for anything else. */
export type ReportSource = Pick<
  ConsoleApi,
  | 'listDevices'
  | 'listJobs'
  | 'listAudit'
  | 'listAlarms'
  | 'listCampaigns'
  | 'listFirmware'
  | 'listUsers'
  | 'getPermissionMatrix'
>;

type Row = Record<string, unknown>;

/** A filter or limit the catalogue does not accept — the fixture's `reference-invalid`. */
export class FixtureReportInputError extends Error {}

const PAGE = 200;

/** Read every page of a cursor-paged list (bounded: a fixture is small, but a loop must still end). */
async function drain<P extends { next_cursor: string | null }>(
  read: (cursor: string | undefined) => Promise<P>,
  rowsOf: (page: P) => Row[],
): Promise<Row[]> {
  const rows: Row[] = [];
  let cursor: string | undefined;
  for (let guard = 0; guard < 1000; guard += 1) {
    const page = await read(cursor);
    rows.push(...rowsOf(page));
    if (page.next_cursor === null) break;
    cursor = page.next_cursor;
  }
  return rows;
}

function compact(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && Object.keys(value as object).length === 0) return null;
  return JSON.stringify(value);
}

const text = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

export function infoOf(reportId: string): ReportInfo {
  const info = FIXTURE_REPORT_CATALOG.items.find((entry) => entry.report_id === reportId);
  if (info === undefined) throw new FixtureReportInputError(`no report with id ${reportId}`);
  return info;
}

/** Mirror of `reports.parse_filters`: unknown names and bad values are refused, empty means unset. */
export function parseFixtureFilters(
  info: ReportInfo,
  raw: Record<string, string>,
): Record<string, string | boolean | number> {
  const known = new Map(info.filters.map((f) => [f.name, f]));
  const out: Record<string, string | boolean | number> = {};
  for (const [name, value] of Object.entries(raw)) {
    const spec = known.get(name);
    if (spec === undefined) {
      const accepted = [...known.keys()].sort().join(', ') || 'none';
      throw new FixtureReportInputError(
        `unknown filter '${name}' for report '${info.report_id}'; it accepts: ${accepted}`,
      );
    }
    const trimmed = value.trim();
    if (trimmed === '') continue;
    if (spec.kind === 'bool') {
      const lowered = trimmed.toLowerCase();
      if (['true', '1', 'yes'].includes(lowered)) out[name] = true;
      else if (['false', '0', 'no'].includes(lowered)) out[name] = false;
      else throw new FixtureReportInputError(`filter '${name}' must be true or false`);
    } else if (spec.kind === 'enum') {
      if (!spec.values.includes(trimmed)) {
        throw new FixtureReportInputError(
          `filter '${name}' must be one of: ${spec.values.join(', ')}`,
        );
      }
      out[name] = trimmed;
    } else if (spec.kind === 'int') {
      if (!/^\d+$/.test(trimmed)) {
        throw new FixtureReportInputError(`filter '${name}' must be a non-negative integer`);
      }
      out[name] = Number(trimmed);
    } else {
      out[name] = trimmed;
    }
  }
  return out;
}

type Filters = Record<string, string | boolean | number>;

async function devicesRows(source: ReportSource, f: Filters): Promise<Row[]> {
  if (f.tag !== undefined) return []; // the fixture fleet carries no tags
  const rows = await drain(
    (cursor) =>
      source.listDevices({
        ...(typeof f.online === 'boolean' ? { online: f.online } : {}),
        ...(typeof f.manufacturer === 'string' ? { manufacturer: f.manufacturer } : {}),
        ...(typeof f.product_class === 'string' ? { model: f.product_class } : {}),
        ...(cursor === undefined ? {} : { cursor }),
        limit: PAGE,
      }),
    (page) => page.items.map((d) => ({ ...d })),
  );
  return rows.map((d) => ({
    acs_device_id: d.acs_device_id,
    serial: d.serial,
    manufacturer: d.manufacturer,
    model: d.model,
    oui: d.oui,
    software_version: d.software_version,
    online: Boolean(d.online),
    last_inform_at: d.last_inform_at ?? null,
    tags: '',
  }));
}

async function fleetSummaryRows(source: ReportSource): Promise<Row[]> {
  const devices = await devicesRows(source, {});
  const groups = new Map<
    string,
    { m: string; model: string; v: string; total: number; on: number }
  >();
  for (const d of devices) {
    const key = JSON.stringify([d.manufacturer, d.model, d.software_version]);
    const g = groups.get(key) ?? {
      m: String(d.manufacturer ?? ''),
      model: String(d.model ?? ''),
      v: String(d.software_version ?? ''),
      total: 0,
      on: 0,
    };
    g.total += 1;
    g.on += d.online === true ? 1 : 0;
    groups.set(key, g);
  }
  return [...groups.values()]
    .sort((a, b) =>
      `${a.m}\u0000${a.model}\u0000${a.v}`.localeCompare(`${b.m}\u0000${b.model}\u0000${b.v}`),
    )
    .map((g) => ({
      manufacturer: g.m,
      model: g.model,
      software_version: g.v,
      devices: g.total,
      online: g.on,
      offline: g.total - g.on,
    }));
}

async function jobsRows(source: ReportSource, f: Filters): Promise<Row[]> {
  const rows = await drain(
    (cursor) =>
      source.listJobs({
        ...(typeof f.device === 'string' ? { device: f.device } : {}),
        ...(typeof f.status === 'string' ? { status: f.status } : {}),
        ...(typeof f.type === 'string' ? { type: f.type } : {}),
        ...(cursor === undefined ? {} : { cursor }),
        limit: PAGE,
      }),
    (page) => page.items.map((j) => ({ ...j })),
  );
  return rows.map((j) => ({
    job_id: j.job_id,
    device_id: j.device_id,
    action: j.action,
    status: j.status,
    requested_at: j.requested_at ?? null,
    started_at: j.started_at ?? null,
    completed_at: j.completed_at ?? null,
    error: compact(j.error),
  }));
}

const AUDIT_LEAD = new Set(['id', 'created_at', 'kind', 'actor', 'actor_kind', 'device_id']);

async function auditRows(source: ReportSource, f: Filters): Promise<Row[]> {
  const rows = await drain(
    (cursor) =>
      source.listAudit({
        ...(typeof f.kind === 'string' ? { kind: f.kind } : {}),
        ...(typeof f.device_id === 'string' ? { device_id: f.device_id } : {}),
        ...(cursor === undefined ? {} : { cursor }),
        limit: PAGE,
      }),
    (page) => page.entries.map((e) => ({ ...e })),
  );
  return rows.map((e) => ({
    created_at: text(e.created_at),
    kind: text(e.kind),
    actor: text(e.actor) ?? text(e.client),
    actor_kind: text(e.actor_kind),
    device_id: text(e.device_id),
    detail: compact(Object.fromEntries(Object.entries(e).filter(([k]) => !AUDIT_LEAD.has(k)))),
  }));
}

async function alarmsRows(source: ReportSource, f: Filters): Promise<Row[]> {
  const rows = await drain(
    (cursor) =>
      source.listAlarms({
        ...(typeof f.severity === 'string' ? { severity: f.severity as 'critical' } : {}),
        ...(typeof f.device === 'string' ? { device: f.device } : {}),
        ...(typeof f.acked === 'boolean' ? { acked: f.acked } : {}),
        ...(cursor === undefined ? {} : { cursor }),
        limit: PAGE,
      }),
    (page) => page.items.map((a) => ({ ...a })),
  );
  return rows.map((a) => ({
    alarm_id: a.alarm_id,
    type: a.type,
    severity: a.severity,
    state: a.state,
    acs_device_id: a.acs_device_id,
    time: a.time,
    acked: Boolean(a.acked),
    escalated: Boolean(a.escalated),
    assignee: text(a.assignee),
    occurrences: typeof a.occurrences === 'number' ? a.occurrences : 1,
    last_seen_at: text(a.last_seen_at),
  }));
}

async function campaignsRows(source: ReportSource, f: Filters): Promise<Row[]> {
  const rows = await drain(
    (cursor) =>
      source.listCampaigns({
        ...(typeof f.status === 'string' ? { status: f.status as 'running' } : {}),
        ...(cursor === undefined ? {} : { cursor }),
        limit: PAGE,
      }),
    (page) => page.items.map((c) => ({ ...c })),
  );
  return rows.map((c) => ({
    campaign_id: c.campaign_id,
    status: c.status,
    file_id: c.file_id,
    brand: c.brand,
    model: c.model,
    version: c.version,
    current_wave: c.current_wave,
    population: c.population,
    created_at: c.created_at ?? null,
    started_at: c.started_at ?? null,
    profile_version: c.profile_version ?? null,
  }));
}

async function firmwareRows(source: ReportSource, f: Filters): Promise<Row[]> {
  const rows = await drain(
    (cursor) =>
      source.listFirmware({
        ...(typeof f.brand === 'string' ? { brand: f.brand } : {}),
        ...(typeof f.model === 'string' ? { model: f.model } : {}),
        ...(cursor === undefined ? {} : { cursor }),
        limit: PAGE,
      }),
    (page) => page.items.map((x) => ({ ...x })),
  );
  return rows.map((x) => ({
    file_id: x.file_id,
    brand: x.brand,
    model: x.model,
    version: x.version,
    size_bytes: x.size_bytes,
    checksum_sha256: x.checksum_sha256,
    status: x.status,
    created_at: x.created_at ?? null,
  }));
}

async function usersRows(source: ReportSource): Promise<Row[]> {
  const { users } = await source.listUsers();
  return users.map((u) => ({
    user_id: u.user_id,
    username: u.username,
    role: u.role,
    status: u.status,
    created_at: u.created_at ?? null,
    updated_at: u.updated_at ?? null,
  }));
}

async function accessMatrixRows(source: ReportSource): Promise<Row[]> {
  const grid = await source.getPermissionMatrix();
  return Object.entries(grid.capabilities).map(([capability, levels]) => ({
    capability,
    ...Object.fromEntries(grid.roles.map((role) => [role, levels[role] ?? 'none'])),
  }));
}

/** All rows of one report, before the row ceiling. Unknown ids and bad filters throw `FixtureReportInputError`. */
export /**
 * W8/G5 — the demo's «CPE پرحرف» rows, counted from the SAME fixture events the events
 * feed shows: the recorded `device.inform` events inside the chosen window, worst-first.
 * The demo has no `acs_informs` collection — the count is what the fixture RETAINS, which is
 * exactly the honesty the live report's description states.
 */
async function chattyRows(f: Filters): Promise<Row[]> {
  const hours = typeof f.hours === 'string' ? parseInt(f.hours, 10) : 1;
  const cutoff = Date.now() - hours * 3600_000;
  const counts = new Map<string, number>();
  for (const event of FIXTURE_EVENTS) {
    if (event.type !== 'io.wicore.acs.device.inform.v1') continue;
    const when = Date.parse(String(event.time ?? ''));
    if (Number.isNaN(when) || when < cutoff) continue;
    const device = String(event.data?.acs_device_id ?? '');
    if (device) counts.set(device, (counts.get(device) ?? 0) + 1);
  }
  const byId = new Map(FIXTURE_DEVICES.map((d) => [d.acs_device_id, d]));
  const rows: Row[] = [];
  for (const [device_id, count] of counts) {
    const device = byId.get(device_id);
    rows.push({
      acs_device_id: device_id,
      serial: device?.serial ?? null,
      manufacturer: device?.manufacturer ?? null,
      informs: count,
      window_hours: hours,
    });
  }
  rows.sort(
    (a, b) =>
      Number(b.informs) - Number(a.informs) ||
      String(a.acs_device_id).localeCompare(String(b.acs_device_id)),
  );
  return rows;
}

async function fixtureReportRows(
  source: ReportSource,
  reportId: string,
  rawFilters: Record<string, string>,
): Promise<{ rows: Row[]; filters: Filters; info: ReportInfo }> {
  const info = infoOf(reportId);
  const filters = parseFixtureFilters(info, rawFilters);
  const read: Record<string, () => Promise<Row[]>> = {
    devices: () => devicesRows(source, filters),
    'fleet-summary': () => fleetSummaryRows(source),
    jobs: () => jobsRows(source, filters),
    audit: () => auditRows(source, filters),
    alarms: () => alarmsRows(source, filters),
    campaigns: () => campaignsRows(source, filters),
    'chatty-cpes': () => chattyRows(filters),
    firmware: () => firmwareRows(source, filters),
    users: () => usersRows(source),
    'access-matrix': () => accessMatrixRows(source),
  };
  const reader = read[reportId];
  if (reader === undefined) throw new FixtureReportInputError(`no report with id ${reportId}`);
  return { rows: await reader(), filters, info };
}

/** The JSON form with the server's ceilings applied (`truncated` is true only when more rows existed). */
export async function fixtureReportResult(
  source: ReportSource,
  reportId: string,
  query: ReportQuery,
  generatedAt: string,
): Promise<ReportResult> {
  const { rows, filters, info } = await fixtureReportRows(source, reportId, query.filters);
  const ceiling = FIXTURE_REPORT_CATALOG.max_rows;
  const cap = query.limit === undefined ? ceiling : Math.min(query.limit, ceiling);
  const kept = rows.slice(0, cap);
  const keys = info.columns.map((c) => c.key);
  return {
    report_id: info.report_id,
    title_fa: info.title_fa,
    generated_at: generatedAt,
    filters,
    columns: info.columns,
    rows: kept.map((row) => Object.fromEntries(keys.map((k) => [k, row[k] ?? null]))),
    row_count: kept.length,
    truncated: rows.length > cap,
  };
}
